import { insiderMembershipState } from "@faire/retailer-shared/lib/retailer";
import {
  useIsLoggedInRetailer,
  useIsLoggedInRetailerOrBrandPreview,
} from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { exhaustiveSwitchWithDefault } from "@faire/web/common/exhaustiveSwitchWithDefault";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  MARKET_FALLBACKS,
  getFaireMarketUtils,
} from "@faire/web/common/promotional-events/getFaireMarketUtils";
import { timestampToMillis } from "@faire/web/common/timestamp";
import { useClock } from "@faire/web/common/useClock";
import { differenceInDays } from "date-fns/differenceInDays";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { useMemo } from "react";

import { MarketState, RetailerSegment } from "./types";
import { useMarketCurrentEvent } from "./useMarketCurrentEvent";
import { useMarketRangeDates } from "./useMarketRangeDates";

export const useMarketStore = () => {
  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const { eventData: _eventData } = useEventsStore(["eventData"]);

  const { currentEventType, isLive, marketName } = useMarketCurrentEvent();
  const { latestMarketEarlyAccessRangeDate, latestMarketMainEventRangeDate } =
    useMarketRangeDates();
  const { now } = useClock();

  const eventData = useMemo(() => {
    return (
      _eventData(currentEventType) ??
      ICurrentPromotionalEventResponse.build({
        start_at: 0,
        end_at: 0,
        insider_early_access_start_at: 0,
      })
    );
  }, [currentEventType, _eventData]);

  const retailerSegment = useMemo(() => {
    if (!isLoggedInRetailerOrBrandPreview) {
      return RetailerSegment.NOT_LOGGED_IN;
    }
    if (
      insiderMembershipState(getRetailer()) ===
      IRetailer.InsiderMembershipState.ELIGIBLE
    ) {
      return RetailerSegment.INSIDER_ELIGIBLE;
    }
    switch (insiderMembershipState(getRetailer())) {
      case IRetailer.InsiderMembershipState.CHURNED:
      case IRetailer.InsiderMembershipState.PAUSED:
      case IRetailer.InsiderMembershipState.UNPAID:
        return RetailerSegment.INSIDER_CHURNED;
      case IRetailer.InsiderMembershipState.ACTIVE:
        return RetailerSegment.INSIDER_ENROLLED;
      default:
        return RetailerSegment.NOT_INSIDER;
    }
  }, [isLoggedInRetailerOrBrandPreview]);

  const mainEventEndAt = useMemo(() => {
    return eventData.end_at
      ? eventData.end_at
      : MARKET_FALLBACKS(strictLocalize).endAtTimestamp;
  }, [eventData, strictLocalize]);

  const earlyAccessStartAt = useMemo(() => {
    return eventData.insider_early_access_start_at
      ? eventData.insider_early_access_start_at
      : MARKET_FALLBACKS(strictLocalize).earlyAccessStartAtTimestamp;
  }, [eventData, strictLocalize]);

  const brandPortalLaunchAt = useMemo(() => {
    return timestampToMillis(eventData.brand_portal_launch_time) ?? Infinity;
  }, [eventData.brand_portal_launch_time]);

  const hasCountdownEnded = now > mainEventEndAt;
  const showMarketLandingPage =
    now >= brandPortalLaunchAt && now < mainEventEndAt;

  const showFaireDirectBanner = showMarketLandingPage;

  const isMarketListLaunched = useMemo(() => {
    const marketListLaunchAt =
      timestampToMillis(eventData.market_list_launch_time) ?? Infinity;
    return now >= marketListLaunchAt && now < mainEventEndAt;
  }, [eventData, mainEventEndAt, now]);

  const showAllSpecials =
    isLoggedInRetailerOrBrandPreview && isMarketListLaunched;

  const showMarketList = showAllSpecials;

  const showBrandLandingPage =
    now >= brandPortalLaunchAt && now < earlyAccessStartAt;

  const mainEventStartAt = useMemo(() => {
    return eventData.start_at
      ? eventData.start_at
      : MARKET_FALLBACKS(strictLocalize).mainEventStartAtTimestamp;
  }, [eventData, strictLocalize]);

  const currentStateIncludingLoggedOut = useMemo(() => {
    if (!mainEventStartAt || !mainEventEndAt || !earlyAccessStartAt) {
      return MarketState.DISABLED;
    }

    if (
      isWithinInterval(now, {
        start: earlyAccessStartAt,
        end: mainEventStartAt,
      })
    ) {
      return MarketState.EARLY_ACCESS;
    }

    if (
      isWithinInterval(now, {
        start: mainEventStartAt,
        end: mainEventEndAt,
      })
    ) {
      return MarketState.MAIN_EVENT;
    }

    return MarketState.DISABLED;
  }, [mainEventStartAt, mainEventEndAt, earlyAccessStartAt, now]);

  const currentState = useMemo(() => {
    if (!isLoggedInRetailerOrBrandPreview) {
      return MarketState.DISABLED;
    }
    return currentStateIncludingLoggedOut;
  }, [currentStateIncludingLoggedOut, isLoggedInRetailerOrBrandPreview]);

  const isPreshowOrLive = useMemo(() => {
    const preShowLaunchAt =
      timestampToMillis(eventData.retailer_pre_show_launch_time) ?? Infinity;
    const endAt = eventData.end_at ?? -Infinity;
    const isPreshow = now >= preShowLaunchAt && now < endAt;
    return isPreshow || isLive;
  }, [eventData, isLive, now]);

  const showNavHeader = showMarketList;

  const isEarlyAccessDay =
    currentStateIncludingLoggedOut === MarketState.EARLY_ACCESS;
  const isInsiderDay =
    retailerSegment === RetailerSegment.INSIDER_ENROLLED && isEarlyAccessDay;

  const nextStateStartAt = useMemo(() => {
    const considerEarlyAccess =
      retailerSegment === RetailerSegment.INSIDER_ENROLLED ||
      retailerSegment === RetailerSegment.INSIDER_ELIGIBLE;
    switch (currentState) {
      case MarketState.DISABLED:
        if (now < earlyAccessStartAt && considerEarlyAccess) {
          return earlyAccessStartAt;
        }
        if (now < mainEventStartAt) {
          return mainEventStartAt;
        }
        return 0;
      case MarketState.EARLY_ACCESS:
        return mainEventStartAt;
      case MarketState.MAIN_EVENT:
        return mainEventEndAt;
      default:
        return exhaustiveSwitchWithDefault(0)(currentState);
    }
  }, [
    currentState,
    earlyAccessStartAt,
    mainEventStartAt,
    mainEventEndAt,
    now,
    retailerSegment,
  ]);

  const formattedEarlyAccessStartDay = useMemo(() => {
    const marketUtils = getFaireMarketUtils(undefined, strictLocalize);
    return marketUtils.formatDateInMarketTimezone(earlyAccessStartAt);
  }, [strictLocalize, earlyAccessStartAt]);

  const formattedLastDay = useMemo(() => {
    const marketUtils = getFaireMarketUtils(undefined, strictLocalize);
    return marketUtils.formatDateInMarketTimezone(mainEventEndAt);
  }, [strictLocalize, mainEventEndAt]);

  const startAt = useMemo(() => {
    switch (retailerSegment) {
      case RetailerSegment.INSIDER_ENROLLED:
        return earlyAccessStartAt;
      default:
        return mainEventStartAt;
    }
  }, [retailerSegment, earlyAccessStartAt, mainEventStartAt]);

  const eventDaysPassed = Math.max(
    0,
    differenceInDays(now, mainEventStartAt.valueOf())
  );

  const eventDaysLeft = Math.max(
    0,
    differenceInDays(mainEventEndAt.valueOf(), now)
  );

  const isLiveForCountdownBanner = isLive;
  const deadlineForCountdownBanner = nextStateStartAt;
  const showCountdownBanner = isPreshowOrLive;
  const isShowingCountdownBanner = isLoggedInRetailer && showCountdownBanner;

  return useMemo(
    () => ({
      currentEventType,
      marketName,
      latestMarketEarlyAccessRangeDate,
      latestMarketMainEventRangeDate,
      retailerSegment,
      showFaireDirectBanner,
      showAllSpecials,
      showMarketList,
      showMarketLandingPage,
      showBrandLandingPage,
      isPreshowOrLive,
      showNavHeader,
      isInsiderDay,
      currentState,
      currentStateIncludingLoggedOut,
      isEarlyAccessDay,
      isMarketListLaunched,
      earlyAccessStartAt,
      formattedEarlyAccessStartDay,
      formattedLastDay,
      mainEventEndAt,
      startAt,
      isLive,
      hasCountdownEnded,
      eventDaysPassed,
      eventDaysLeft,
      isLiveForCountdownBanner,
      deadlineForCountdownBanner,
      showCountdownBanner,
      isShowingCountdownBanner,
    }),
    [
      currentEventType,
      marketName,
      latestMarketEarlyAccessRangeDate,
      latestMarketMainEventRangeDate,
      retailerSegment,
      showFaireDirectBanner,
      showAllSpecials,
      showMarketList,
      showMarketLandingPage,
      showBrandLandingPage,
      isPreshowOrLive,
      showNavHeader,
      isInsiderDay,
      currentState,
      currentStateIncludingLoggedOut,
      isEarlyAccessDay,
      isMarketListLaunched,
      earlyAccessStartAt,
      formattedEarlyAccessStartDay,
      formattedLastDay,
      mainEventEndAt,
      startAt,
      isLive,
      hasCountdownEnded,
      eventDaysPassed,
      eventDaysLeft,
      isLiveForCountdownBanner,
      deadlineForCountdownBanner,
      showCountdownBanner,
      isShowingCountdownBanner,
    ]
  );
};
